/**
 * NiiVue exactly as it ships: its own multiplanar layout (axial, coronal,
 * sagittal) plus the 3D render, its own crosshair, mouse and keyboard
 * handling. Nothing here restyles it — the component only hands it volumes.
 */

import * as React from "react";
import { MULTIPLANAR_TYPE, Niivue, SHOW_RENDER, SLICE_TYPE } from "@niivue/niivue";

export interface NiivueLayer {
  url: string;
  name: string;
  colormap: string;
}

const OVERLAY_COLORMAPS = ["red", "green", "blue", "warm", "violet"];

export function overlayColormap(index: number): string {
  return OVERLAY_COLORMAPS[index % OVERLAY_COLORMAPS.length];
}

export function NiivueView({ base, overlays }: { base: NiivueLayer | null; overlays: NiivueLayer[] }) {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const nvRef = React.useRef<Niivue | null>(null);
  /** Resolves once NiiVue owns the canvas; loading before that has no shaders yet. */
  const readyRef = React.useRef<Promise<void> | null>(null);
  const [location, setLocation] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // A fresh canvas per mount: a WebGL context released by the previous
    // mount (React re-runs effects in development) cannot be attached again.
    const host = hostRef.current;
    if (!host) return;
    const canvas = document.createElement("canvas");
    canvas.className = "absolute inset-0 h-full w-full";
    host.appendChild(canvas);
    const nv = new Niivue({ backColor: [0, 0, 0, 1], show3Dcrosshair: true, dragAndDropEnabled: false, logLevel: "error" });
    nvRef.current = nv;
    // Antialiasing off, as the classic viewer does: some software GL stacks
    // reject the multisampled framebuffer.
    readyRef.current = nv.attachToCanvas(canvas, false).then(() => {
      nv.setSliceType(SLICE_TYPE.MULTIPLANAR);
      nv.opts.multiplanarShowRender = SHOW_RENDER.ALWAYS;
      nv.opts.multiplanarLayout = MULTIPLANAR_TYPE.GRID;
      nv.drawScene();
    });
    nv.onLocationChange = (data: unknown) => {
      const d = data as { string?: string };
      setLocation(d.string ?? "");
    };
    return () => {
      nvRef.current = null;
      try {
        nv.gl?.getExtension("WEBGL_lose_context")?.loseContext();
      } catch {
        // Context already gone.
      }
      canvas.remove();
    };
  }, []);

  const key = [base?.url, ...overlays.map((o) => o.url + o.colormap)].join("|");
  React.useEffect(() => {
    const nv = nvRef.current;
    if (!nv) return;
    let live = true;
    setError(null);
    // NiiVue picks its reader from the file name; blob: URLs carry none.
    const layers = base
      ? [
          { url: base.url, name: base.name, colormap: base.colormap, opacity: 1 },
          ...overlays.map((o) => ({ url: o.url, name: o.name, colormap: o.colormap, opacity: 0.6 })),
        ]
      : [];
    void (readyRef.current ?? Promise.resolve())
      .then(() => nv.loadVolumes(layers))
      .then(() => {
        if (!live) return;
        nv.setSliceType(SLICE_TYPE.MULTIPLANAR);
        nv.opts.multiplanarShowRender = SHOW_RENDER.ALWAYS;
        nv.drawScene();
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-black">
      <div className="relative min-h-0 flex-1">
        <div ref={hostRef} className="absolute inset-0" />
        {error ? (
          <p className="absolute inset-x-0 top-4 text-center text-[11px] text-red-300">{error}</p>
        ) : null}
      </div>
      <div className="shrink-0 border-t border-neutral-800 bg-neutral-950 px-3 py-1 font-mono text-[10.5px] text-neutral-400">
        {location || "Click to move the crosshair · drag in 3D to rotate · scroll to change slice"}
      </div>
    </div>
  );
}
